import { useCallback, useMemo, useState } from "react";

/** The value every filter starts on, and what "no constraint" means for one. */
export const ALL = "all";

export interface RecommendationFilterOptionSpec {
  /** Value written back through `onChange` when this option is picked. */
  value: string;
  /** Human-readable option text. */
  label: string;
}

export interface RecommendationFilterOption extends RecommendationFilterOptionSpec {
  /** How many items picking this option would yield, given the other filters. */
  count: number;
}

/**
 * One dropdown: how an item is tested against the selected value, and which option it counts
 * towards. Filtering, counting and rendering all read this one descriptor, so each filter is
 * defined in exactly one place.
 */
export interface RecommendationFilterSpec<T> {
  /** Stable key for this filter in the values/options records. */
  key: string;
  /** Text rendered above the control. */
  label: string;
  /** Options to offer, in display order. `ALL` is prepended automatically. */
  options: RecommendationFilterOptionSpec[];
  /** Label for this filter's `ALL` option, e.g. "All Statuses". */
  allLabel: string;
  /** Whether _item_ satisfies this filter at _value_. */
  matches: (item: T, value: string) => boolean;
  /** Which option _item_ counts towards. */
  valueOf: (item: T) => string;
}

export interface RecommendationFilterState<T> {
  /** The items satisfying every filter. */
  filtered: T[];
  /** Per-filter options with live counts, keyed by filter key. */
  options: Record<string, RecommendationFilterOption[]>;
  /** The selected value per filter, keyed by filter key. */
  values: Record<string, string>;
  /** Whether any filter is set to something other than `ALL`. */
  hasActiveFilter: boolean;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
}

const withAll = <T>(filter: RecommendationFilterSpec<T>): RecommendationFilterOptionSpec[] => [
  { value: ALL, label: filter.allLabel },
  ...filter.options,
];

/**
 * Drive a set of independent dropdown filters over a list, with live option counts.
 *
 * Each dropdown's counts reflect the _other_ filters, so an option always shows how many items
 * selecting it would actually yield. Tallied in one pass: an item counts towards a dropdown
 * whenever it satisfies every filter but that one — which is O(items x filters) rather than one
 * full filter pass per option.
 *
 * @example
 *   const FILTERS = [
 *     {
 *       key: "status",
 *       label: "Status",
 *       allLabel: "All Statuses",
 *       options: [{ value: "open", label: "Open" }],
 *       matches: (rec, value) => value === ALL || rec.status === value,
 *       valueOf: (rec) => rec.status,
 *     },
 *   ];
 *   const { filtered, options, values, setFilter } = useRecommendationFilters(recs, FILTERS);
 *
 * @param items - The full list, unfiltered.
 * @param filters - One descriptor per dropdown. Must be a stable reference (module scope or
 *   memoized), since it keys the memoized filtering and counting.
 */
export const useRecommendationFilters = <T>(
  items: T[],
  filters: RecommendationFilterSpec<T>[],
): RecommendationFilterState<T> => {
  const noFilters = useMemo(
    () => Object.fromEntries(filters.map(({ key }) => [key, ALL])),
    [filters],
  );
  const [values, setValues] = useState<Record<string, string>>(noFilters);

  // `values` is seeded from `noFilters` once, so a filter added after mount has no entry in it.
  // Reading a missing key as ALL makes that mean "unconstrained"; reading it as `undefined` would
  // fail every `matches` and silently empty the list with no way for the reader to tell why.
  const valueFor = useCallback((key: string) => values[key] ?? ALL, [values]);

  const filtered = useMemo(
    () => items.filter((item) => filters.every(({ key, matches }) => matches(item, valueFor(key)))),
    [items, filters, valueFor],
  );

  const options = useMemo(() => {
    const tallies = filters.map(() => new Map<string, number>());
    const totals = filters.map(() => 0);

    for (const item of items) {
      const matched = filters.map(({ key, matches }) => matches(item, valueFor(key)));

      filters.forEach(({ valueOf }, index) => {
        if (!matched.every((isMatch, other) => other === index || isMatch)) {
          return;
        }

        totals[index] += 1;
        const value = valueOf(item);
        tallies[index].set(value, (tallies[index].get(value) ?? 0) + 1);
      });
    }

    return Object.fromEntries(
      filters.map((filter, index) => [
        filter.key,
        withAll(filter).map((option) => ({
          ...option,
          count: option.value === ALL ? totals[index] : (tallies[index].get(option.value) ?? 0),
        })),
      ]),
    );
  }, [items, filters, valueFor]);

  const setFilter = useCallback(
    (key: string, value: string) => setValues((previous) => ({ ...previous, [key]: value })),
    [],
  );
  const clearFilters = useCallback(() => setValues(noFilters), [noFilters]);

  // Handed out with every key filled in, so the controlled <select> driving each dropdown never
  // sees `undefined` and flips itself to uncontrolled.
  const selected = useMemo(
    () => Object.fromEntries(filters.map(({ key }) => [key, valueFor(key)])),
    [filters, valueFor],
  );

  return {
    filtered,
    options,
    values: selected,
    hasActiveFilter: filters.some(({ key }) => valueFor(key) !== ALL),
    setFilter,
    clearFilters,
  };
};
